/**
 * @file Reusable file drop zone component
 * @version 1.0.0
 * @author Claude Assistant
 * @since 2025-07-10
 */

const { ref, computed } = Vue;

export default {
  name: 'FileDropzone',

  template: `
    <div 
      :class="dropZoneClasses"
      @dragover.prevent="handleDragOver"
      @dragleave.prevent="handleDragLeave"
      @drop.prevent="handleDrop"
      @click="triggerFileInput"
    >
      <input 
        ref="fileInput"
        type="file"
        :accept="acceptedFormats.map(f => '.' + f).join(',')"
        @change="handleFileSelect"
        class="d-none"
      >
      
      <div class="text-center p-4">
        <i class="bi-cloud-arrow-up-fill text-primary" style="font-size: 2rem;"></i>
        <h6 class="mt-2 mb-1">{{ title }}</h6>
        <small class="text-muted">{{ subtitle }}</small>
      </div>
    </div>
  `,

  props: {
    title: {
      type: String,
      default: 'Drop file here or click to browse',
    },
    subtitle: {
      type: String,
      default: 'Select a file to upload',
    },
    acceptedFormats: {
      type: Array,
      default: () => ['json'],
    },
    maxFileSize: {
      type: Number,
      default: 10 * 1024 * 1024, // 10MB
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['file-selected', 'file-error'],

  setup(props, { emit }) {
    const fileInput = ref(null);
    const isDragOver = ref(false);

    const dropZoneClasses = computed(() => {
      return [
        'border',
        'border-2',
        'rounded',
        'p-4',
        'text-center',
        props.disabled ? 'disabled' : 'cursor-pointer',
        isDragOver.value
          ? 'border-primary bg-primary bg-opacity-10'
          : 'border-secondary',
      ].join(' ');
    });

    const handleDragOver = event => {
      if (props.disabled) return;
      isDragOver.value = true;
    };

    const handleDragLeave = event => {
      if (props.disabled) return;
      isDragOver.value = false;
    };

    const handleDrop = event => {
      if (props.disabled) return;
      isDragOver.value = false;
      const files = event.dataTransfer.files;
      if (files.length > 0) {
        handleFileSelection(files[0]);
      }
    };

    const triggerFileInput = () => {
      if (props.disabled) return;
      fileInput.value.click();
    };

    const handleFileSelect = event => {
      const file = event.target.files[0];
      if (file) {
        handleFileSelection(file);
      }
    };

    const handleFileSelection = file => {
      if (!validateFile(file)) {
        return;
      }
      emit('file-selected', file);
    };

    const validateFile = file => {
      const fileExtension = file.name.split('.').pop().toLowerCase();

      if (!props.acceptedFormats.includes(fileExtension)) {
        emit(
          'file-error',
          `Please select a ${props.acceptedFormats.join(' or ')} file`
        );
        return false;
      }

      if (file.size > props.maxFileSize) {
        emit(
          'file-error',
          `File must be smaller than ${formatFileSize(props.maxFileSize)}`
        );
        return false;
      }

      return true;
    };

    const formatFileSize = bytes => {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    return {
      fileInput,
      isDragOver,
      dropZoneClasses,
      handleDragOver,
      handleDragLeave,
      handleDrop,
      triggerFileInput,
      handleFileSelect,
    };
  },
};
